import type {
  EnhancedConfigTypeId,
  EnhancedLanguageId,
  EnhancedManCodeId,
  EnhancedMarketId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import {
  type ApiConfiguration,
  type ConfigurationMode,
  type FullApiConfiguration,
  isTg3ApiConfiguration,
  type ManCodeApiConfiguration,
  type Tg3ApiConfiguration,
} from '#features/Configuration/ApiConfiguration.ts'
import { lookupMarketAndLanguageMapping } from '#features/Markets/lookupMarketAndLanguageMapping'
import { isLocalConfigurationId } from '#pages/Configuration/components/useActiveConfiguration'
import { isValidDateString, isValidPeriod, periodToString, pricingDateToString, stringToPeriod, stringToPricingDate } from '#utils/DateString'
import { isManCodeId } from '#utils/validateManCodeId'

export const AllowedConfigurationModes: ConfigurationMode[] = ['writable', 'read-only', 'stashed', 'cloned']

export const mapConfigurationModeToApiMode: Record<ConfigurationMode, 'R' | 'W' | 'S' | 'C'> = {
  writable: 'W',
  'read-only': 'R',
  stashed: 'S',
  cloned: 'C',
}

export const mapApiModeToConfigurationMode: Partial<Record<string, ConfigurationMode>> = {
  W: 'writable',
  R: 'read-only',
  S: 'stashed',
  C: 'cloned',
}

function checkFields(
  requiredFields: { name: string; value: unknown }[],
  numberFields: { name: string; value: number | undefined }[],
  dateFields: { name: string; value: string }[],
  modeFields: { name: string; value: string }[],
): boolean {
  const missingFields = requiredFields.filter((field) => field.value === null || field.value === undefined)
  const invalidNumberFields = numberFields.filter((field) => typeof field.value === 'number' && Number.isNaN(field.value))
  const invalidDateFields = dateFields.filter((field) => !isValidDateString(field.value))
  const invalidModeFields = modeFields.filter((field) => !AllowedConfigurationModes.includes(field.value as ConfigurationMode))

  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.map((field) => field.name).join(', ')}`)
  }
  if (invalidNumberFields.length > 0) {
    throw new Error(`Invalid number fields: ${invalidNumberFields.map((field) => field.name).join(', ')}`)
  }
  if (invalidDateFields.length > 0) {
    throw new Error(`Invalid date fields: ${invalidDateFields.map((field) => field.name).join(', ')}`)
  }
  if (invalidModeFields.length > 0) {
    throw new Error(`Invalid mode fields: ${invalidModeFields.map((field) => field.name).join(', ')}`)
  }

  return true
}

export const makeCloseApiFromSearch = (configuration: TruckConfiguration) => {
  return { closeConfiguration: configuration.id }
}

export const closeApiFromSearch = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const possibleId = searchParams.get('closeConfiguration') ?? undefined
  return isLocalConfigurationId(possibleId) ? possibleId : undefined
}

const parseManCodeSourceType = (text: string | null): Exclude<ManCodeApiConfiguration['source'], undefined> | null => {
  if (text === null) {
    return null
  }
  // allows the compiler to fail when new sources are added
  switch (text as Exclude<ManCodeApiConfiguration['source'], undefined>) {
    case 'reference-truck':
      return 'reference-truck'
    case 'user-input':
      return 'user-input'
    case 'restart':
      return 'restart'
    case 'code-info-overlay':
      return 'code-info-overlay'
  }
  return null
}

export async function parseApiConfigurationFromSearch(): Promise<ApiConfiguration | undefined> {
  const searchParams = new URLSearchParams(window.location.search)

  if (searchParams.size === 0) {
    return undefined
  }

  const countryCode = searchParams.get('orderCountry') ?? undefined
  const languageCode = searchParams.get('language') ?? searchParams.get('orderLanguage') ?? undefined

  const partPeriodStr = searchParams.get('partPeriod') ?? undefined
  const partPeriod = isValidPeriod(partPeriodStr) ? periodToString(partPeriodStr) : undefined
  const marketIdStr = searchParams.get('marketId') ?? undefined
  const nationalCodeMarketIdOverrideStr = searchParams.get('_nationalCodeMarketIdOverride') ?? undefined
  const vehicleTypeIdStr = searchParams.get('vehicleTypeId') ?? undefined
  const viewIdStr = searchParams.get('viewId') ?? undefined
  const backUrl = searchParams.get('backUrl') ?? undefined
  const configurationTypeIdStr = searchParams.get('configurationTypeId') ?? undefined
  const languageIdStr = searchParams.get('languageId') ?? undefined
  const manCode = searchParams.get('manCode') ?? undefined
  const tg3Code = searchParams.get('tg3Code') ?? undefined
  const callingSystem = searchParams.get('callingSystem') ?? undefined
  const modeStr = searchParams.get('mode') ?? undefined
  const mode = modeStr ? (mapApiModeToConfigurationMode[modeStr] ?? 'unknown') : undefined
  const pricingDateStr = searchParams.get('pricingDate') ?? undefined
  const pricingDate = pricingDateStr ? pricingDateToString(pricingDateStr) : undefined
  const stepStr = searchParams.get('step') ?? undefined
  const desiredDealerDateStr = searchParams.get('desiredDealerDate') ?? undefined
  const desiredDealerDate = desiredDealerDateStr ? pricingDateToString(desiredDealerDateStr) : undefined
  const marketIdFromSearch = marketIdStr ? Number.parseInt(marketIdStr, 10) : undefined
  const languageIdFromSearch = languageIdStr ? Number.parseInt(languageIdStr, 10) : undefined
  const titleFromSearch = searchParams.get('title') ?? undefined

  if (tg3Code) {
    return {
      tg3Code,
    } satisfies Tg3ApiConfiguration
  }

  const {
    marketId: marketIdFromMapping,
    languageId: languageIdFromMapping,
    viewId: viewIdFromMapping,
    vehicleTypeId: vehicleTypeIdFromMapping,
    configurationTypeId: configurationTypeIdFromMapping,
  } = await lookupMarketAndLanguageMapping(countryCode ?? marketIdFromSearch, languageCode ?? languageIdFromSearch)

  // parse number values
  const marketId = marketIdFromSearch !== undefined ? marketIdFromSearch : marketIdFromMapping
  const nationalCodeMarketIdOverride = nationalCodeMarketIdOverrideStr ? Number.parseInt(nationalCodeMarketIdOverrideStr, 10) : undefined
  const vehicleTypeId = vehicleTypeIdStr ? Number.parseInt(vehicleTypeIdStr, 10) : vehicleTypeIdFromMapping
  const viewId = viewIdStr ? Number.parseInt(viewIdStr, 10) : viewIdFromMapping
  const languageId = languageIdFromSearch !== undefined ? languageIdFromSearch : languageIdFromMapping
  const configurationTypeId = configurationTypeIdStr ? Number.parseInt(configurationTypeIdStr, 10) : configurationTypeIdFromMapping
  const step = stepStr ? Number.parseInt(stepStr, 10) : undefined
  const stepValue = step === 0 || step === 1 ? 'GeneralStep' : step === 2 || step === 3 ? 'DetailedStep' : undefined

  const numberFields = [
    marketId && { name: 'marketId', value: marketId },
    vehicleTypeId && { name: 'vehicleTypeId', value: vehicleTypeId },
    viewId && { name: 'viewId', value: viewId },
    configurationTypeId && { name: 'configurationTypeId', value: configurationTypeId },
    languageId && { name: 'languageId', value: languageId },
    step && { name: 'step', value: stepValue },
    nationalCodeMarketIdOverride && { name: '_nationalCodeMarketIdOverride', value: nationalCodeMarketIdOverride },
  ].filter(Boolean) as { name: string; value: number }[]

  const dateFields = [
    partPeriod && { name: 'partPeriod', value: partPeriod },
    pricingDate && { name: 'pricingDate', value: pricingDate },
    desiredDealerDate && { name: 'desiredDealerDate', value: desiredDealerDate },
  ].filter(Boolean) as { name: string; value: string }[]

  const modeFields = [mode && { name: 'mode', value: mode }].filter(Boolean) as { name: string; value: string }[]

  if (!manCode) {
    const requiredFields = [
      { name: 'marketId', value: marketId },
      { name: 'vehicleTypeId', value: vehicleTypeId },
      { name: 'viewId', value: viewId },
    ]

    checkFields(requiredFields, numberFields, dateFields, [])

    if (!(desiredDealerDate || partPeriod)) {
      throw new Error('Missing required fields: please define either desiredDealerDate or partPeriod')
    }

    if (partPeriod && desiredDealerDate) {
      console.warn('Invalid fields: please define either desiredDealerDate or partPeriod, not both, will use desiredDealerDate')
    }

    return {
      backUrl,
      callingSystem,
      pricingDate,
      partPeriod: !desiredDealerDate ? partPeriod : undefined,
      desiredDealerDate,
      nationalCodeMarketIdOverride: nationalCodeMarketIdOverride as EnhancedMarketId,
      step: stepValue,
      market: marketId as EnhancedMarketId,
      vehicleType: vehicleTypeId as EnhancedVehicleTypeId,
      view: viewId as EnhancedViewId,
      language: languageId as EnhancedLanguageId | undefined,
      configurationType: configurationTypeId as EnhancedConfigTypeId | undefined,
      mode: mode as ConfigurationMode | undefined,
      orderCountry: countryCode,
      orderLanguage: languageCode,
      title: titleFromSearch,
    } satisfies FullApiConfiguration
  }
  const requiredFields = [{ name: 'manCode', value: manCode }]
  checkFields(requiredFields, numberFields, dateFields, modeFields)

  const isValidManCodeId = isManCodeId(manCode)

  if (!isValidManCodeId) {
    throw new Error('Invalid field `manCode`: should be TMS-XXXXXXXX where X is number or uppercase letter')
  }

  return {
    backUrl,
    callingSystem,
    pricingDate,
    partPeriod: !desiredDealerDate ? partPeriod : undefined,
    desiredDealerDate,
    nationalCodeMarketIdOverride: nationalCodeMarketIdOverride as EnhancedMarketId,
    manCode: manCode as EnhancedManCodeId,
    step: stepValue,
    market: marketId as EnhancedMarketId | undefined,
    vehicleType: vehicleTypeId as EnhancedVehicleTypeId | undefined,
    configurationType: configurationTypeId as EnhancedConfigTypeId | undefined,
    view: viewId as EnhancedViewId | undefined,
    language: languageId as EnhancedLanguageId | undefined,
    mode: mode as ConfigurationMode,
    orderCountry: countryCode,
    orderLanguage: languageCode,
    source: parseManCodeSourceType(searchParams.get('source')) ?? undefined,
    title: titleFromSearch,
  } satisfies ManCodeApiConfiguration
}

export function searchParamsFromApiConfig({
  closeConfiguration,
  apiConfig,
  options,
}: {
  closeConfiguration?: TruckConfiguration
  apiConfig: ApiConfiguration
  options?: {
    skipOptionalsWhenOrderCountrySet: boolean
    skipLanguageWhenOrderCountrySet: boolean
    dateSelectionMode?: 'delivery' | 'part'
  }
}): URLSearchParams {
  const searchParams = new URLSearchParams()
  const skipOrderCountryOptionals = options?.skipOptionalsWhenOrderCountrySet ? apiConfig.orderCountry !== undefined : false
  const skipOrderLanguageOptionals = options?.skipLanguageWhenOrderCountrySet ? apiConfig.orderCountry !== undefined : false

  if (closeConfiguration) {
    searchParams.set('closeConfiguration', closeConfiguration.id)
  }

  if (isTg3ApiConfiguration(apiConfig)) {
    searchParams.set('tg3Code', apiConfig.tg3Code)
    return searchParams
  }

  if (apiConfig.partPeriod && (!options?.dateSelectionMode || options.dateSelectionMode === 'part')) {
    searchParams.set('partPeriod', stringToPeriod(apiConfig.partPeriod))
  }
  if (apiConfig.desiredDealerDate && (!options?.dateSelectionMode || options.dateSelectionMode === 'delivery')) {
    searchParams.set('desiredDealerDate', stringToPricingDate(apiConfig.desiredDealerDate))
  }
  if (typeof apiConfig.orderCountry === 'string') {
    searchParams.set('orderCountry', apiConfig.orderCountry)
  }
  if (typeof apiConfig.orderLanguage === 'string') {
    searchParams.set('language', apiConfig.orderLanguage)
  }
  if (!skipOrderCountryOptionals && typeof apiConfig.market === 'number') {
    searchParams.set('marketId', String(apiConfig.market))
  }
  if (!skipOrderCountryOptionals && typeof apiConfig.vehicleType === 'number') {
    searchParams.set('vehicleTypeId', String(apiConfig.vehicleType))
  }
  if (!skipOrderCountryOptionals && typeof apiConfig.view === 'number') {
    searchParams.set('viewId', String(apiConfig.view))
  }
  if (apiConfig.backUrl) {
    searchParams.set('backUrl', apiConfig.backUrl)
  }
  if (!skipOrderCountryOptionals && typeof apiConfig.configurationType === 'number') {
    searchParams.set('configurationTypeId', String(apiConfig.configurationType))
  }
  if (!skipOrderLanguageOptionals && typeof apiConfig.language === 'number') {
    searchParams.set('languageId', String(apiConfig.language))
  }
  if (apiConfig.manCode) {
    searchParams.set('manCode', apiConfig.manCode)
  }
  if (apiConfig.callingSystem) {
    searchParams.set('callingSystem', apiConfig.callingSystem)
  }
  if (apiConfig.mode) {
    searchParams.set('mode', mapConfigurationModeToApiMode[apiConfig.mode])
  }
  if (apiConfig.pricingDate) {
    searchParams.set('pricingDate', stringToPricingDate(apiConfig.pricingDate))
  }
  if (apiConfig.nationalCodeMarketIdOverride) {
    searchParams.set('_nationalCodeMarketIdOverride', String(apiConfig.nationalCodeMarketIdOverride))
  }
  if (apiConfig.title) {
    searchParams.set('title', apiConfig.title)
  }
  if (apiConfig.source) {
    searchParams.set('source', apiConfig.source)
  }

  return searchParams
}
